import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { 
  FolderOpen, 
  Edit, 
  Trash2, 
  Eye, 
  Search,
  Filter,
  MoreVertical,
  Users,
  CheckCircle,
  Clock,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { Row, Col, Button, Form, Table, Card, Badge, InputGroup, Dropdown, ProgressBar, Pagination, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import ImprovedPagination from './common/ImprovedPagination';
import './Dashboard.css';
import './Projects.css';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all'); // 'all', 'company', 'retailer'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    // Load itemsPerPage from localStorage (persists across logins)
    const saved = localStorage.getItem('projectsItemsPerPage');
    return saved ? parseInt(saved, 10) : 25;
  });
  const [paginationInfo, setPaginationInfo] = useState({
    totalCount: 0,
    totalPages: 0,
    page: 1,
    limit: 25
  });
  const STORAGE_KEY = 'projectsListState';
  const navigate = useNavigate();
  const skipDebouncedPageReset = useRef(true);

  // Fetch projects from API with pagination
  const fetchProjects = async (page = currentPage, limit = itemsPerPage) => {
    try {
      setLoading(true);
      setError(null);
      
      // Pass type filter to backend if specified
      const typeParam = filterType !== 'all' ? filterType : undefined;
      const statusParam = filterStatus !== 'all' ? filterStatus : undefined;
      const searchParam = debouncedSearch.trim() ? debouncedSearch.trim() : undefined;
      const response = await apiService.getAllProjects(page, limit, typeParam, statusParam, searchParam);
      
      if (response.success && Array.isArray(response.data)) {
        setProjects(response.data);
        // Update pagination info from backend response
        setPaginationInfo({
          totalCount: response.totalCount || 0,
          totalPages: response.totalPages || 0,
          page: response.page || page,
          limit: response.limit || limit
        });
      } else {
        setError('Failed to fetch projects');
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError(error.message || 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const savedRaw = sessionStorage.getItem(STORAGE_KEY);
    if (savedRaw) {
      try {
        const saved = JSON.parse(savedRaw);
        if (typeof saved.searchTerm === 'string') setSearchTerm(saved.searchTerm);
        if (typeof saved.filterStatus === 'string') setFilterStatus(saved.filterStatus);
        if (typeof saved.filterType === 'string') setFilterType(saved.filterType);
        if (typeof saved.currentPage === 'number') setCurrentPage(saved.currentPage);
        if (typeof saved.itemsPerPage === 'number') setItemsPerPage(saved.itemsPerPage);
      } catch (err) {
        console.error('Failed to restore projects state', err);
      }
    }

    // Force Poppins font on all elements in this component
    const style = document.createElement('style');
    style.textContent = `
      .projects-wrapper *,
      .projects-wrapper *::before,
      .projects-wrapper *::after {
        font-family: 'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useLayoutEffect(() => {
    if (skipDebouncedPageReset.current) {
      skipDebouncedPageReset.current = false;
      return;
    }
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Fetch projects when page, filters, or items per page changes (server applies status/search)
  useEffect(() => {
    fetchProjects(currentPage, itemsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, filterType, filterStatus, debouncedSearch]);

  // Persist itemsPerPage to localStorage (survives logins)
  useEffect(() => {
    try {
      localStorage.setItem('projectsItemsPerPage', itemsPerPage.toString());
    } catch (err) {
      console.error('Failed to persist itemsPerPage', err);
    }
  }, [itemsPerPage]);

  // Persist table state for this session
  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          searchTerm,
          filterStatus,
          filterType,
          currentPage,
          itemsPerPage
        })
      );
    } catch (err) {
      console.error('Failed to persist projects table state', err);
    }
  }, [searchTerm, filterStatus, filterType, currentPage, itemsPerPage, STORAGE_KEY]);

  const handleDeleteProject = (projectId) => {
    setProjects(prevProjects => prevProjects.filter(project => project.id !== projectId));
  };

  const handleViewProject = (project) => {
    navigate(`/company/${project.companyId}/project/${project.id}`);
  };

  const handleRefresh = () => {
    fetchProjects(currentPage, itemsPerPage);
  };

  const totalPages = paginationInfo.totalPages || 1;
  const totalItems = paginationInfo.totalCount || 0;
  const currentProjects = projects;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active':
        return <Badge bg="success">{status}</Badge>;
      case 'Completed':
        return <Badge bg="success">{status}</Badge>;
      case 'In Progress':
        return <Badge bg="primary">{status}</Badge>;
      case 'Planning':
        return <Badge bg="warning" text="dark">{status}</Badge>;
      case 'On Hold':
        return <Badge bg="secondary">{status}</Badge>;
      default:
        return <Badge bg="light" text="dark">{status}</Badge>;
    }
  };


  return (
    <div className="projects-wrapper">
      <div className="modern-dashboard">
        {/* Enhanced Header */}
        <div className="dashboard-header-section mb-4">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h1 className="dashboard-title">Projects Management</h1>
              <p className="dashboard-subtitle">Track and manage your project portfolio</p>
            </div>
            <div className="dashboard-actions">
              <Button variant="outline-primary" className="me-2">
                <Eye size={16} className="me-1" />
                View Reports
              </Button>
              <Button variant="outline-secondary" className="me-2">
                <Filter size={16} className="me-1" />
                Advanced Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Loading and Error States */}
        {loading && (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" />
            <span className="ms-3">Loading projects...</span>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="mb-4">
            <AlertCircle size={20} className="me-2" />
            {error}
            <Button variant="outline-danger" size="sm" className="ms-3" onClick={handleRefresh}>
              <RefreshCw size={16} className="me-1" />
              Try Again
            </Button>
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* Stats Cards */}
            <Row className="mb-4">
          <Col lg={3} md={6} className="mb-3">
            <Card className="modern-stat-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="stat-content">
                    <p className="stat-title">Total Projects</p>
                    <h3 className="stat-value">{totalItems}</h3>
                    <div className="stat-change">
                      <span className="change-text">All time</span>
                    </div>
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }}>
                    <FolderOpen className="text-white" size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="modern-stat-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="stat-content">
                    <p className="stat-title">Active Projects</p>
                    <h3 className="stat-value">{projects.filter(project => project.status === 'In Progress').length}</h3>
                    <div className="stat-change">
                      <span className="change-text">On current page</span>
                    </div>
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                    <Clock className="text-white" size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="modern-stat-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="stat-content">
                    <p className="stat-title">Completed</p>
                    <h3 className="stat-value">{projects.filter(project => project.status === 'Completed').length}</h3>
                    <div className="stat-change">
                      <span className="change-text">On current page</span>
                    </div>
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
                    <CheckCircle className="text-white" size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="modern-stat-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="stat-content">
                    <p className="stat-title">On This Page</p>
                    <h3 className="stat-value">{projects.length}</h3>
                    <div className="stat-change">
                      <span className="change-text">Projects shown</span>
                    </div>
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                    <FolderOpen className="text-white" size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Enhanced Horizontal Controls */}
        <Card className="mb-4 project-controls-card">
          <Card.Body className="py-3">
            <div className="d-flex flex-wrap align-items-center gap-3">
              {/* Search */}
              <div className="project-search-box">
                <InputGroup style={{ minWidth: '280px' }}>
                  <InputGroup.Text>
                    <Search size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by project name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </div>

              {/* Status Filter */}
              <div className="project-filter-select">
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ minWidth: '160px' }}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="planning">Planning</option>
                  <option value="in progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="on hold">On Hold</option>
                </Form.Select>
              </div>

              {/* Type Filter */}
              <div className="project-filter-select">
                <Form.Select
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    setCurrentPage(1); // Reset to first page when filter changes
                  }}
                  style={{ minWidth: '140px' }}
                >
                  <option value="all">All Types</option>
                  <option value="company">Company</option>
                  <option value="retailer">Retailer</option>
                </Form.Select>
              </div>

              {/* Quick Actions */}
              <div className="project-quick-actions">
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => window.print()}
                >
                  <Eye size={14} className="me-1" />
                  Print View
                </Button>
              </div>

              {/* Clear Filters */}
              {(searchTerm || filterStatus !== 'all' || filterType !== 'all') && (
                <Button 
                  variant="outline-danger" 
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterStatus('all');
                    setFilterType('all');
                  }}
                >
                  Clear All
                </Button>
              )}
            </div>
            
            {/* Pagination moved to search panel */}
            <div className="d-flex justify-content-between align-items-center mt-3 pt-3 flex-wrap gap-3" style={{ borderTop: '1px solid #e9ecef', paddingTop: '1rem' }}>
              {/* Items per page selector */}
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                  Show:
                </span>
                <Form.Select
                  value={itemsPerPage}
                  onChange={(e) => {
                    const newItemsPerPage = parseInt(e.target.value, 10);
                    setItemsPerPage(newItemsPerPage);
                    setCurrentPage(1);
                    // fetchProjects will be called by useEffect when itemsPerPage changes
                  }}
                  size="sm"
                  style={{ width: 'auto', minWidth: '80px' }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
                <span className="text-muted" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                  per page
                </span>
              </div>
              
              {/* Pagination controls */}
              {totalPages > 1 && (
                <ImprovedPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(page) => setCurrentPage(page)}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                />
              )}
            </div>
          </Card.Body>
        </Card>

        {/* Enhanced Projects Table */}
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center py-3">
            <div className="d-flex align-items-center gap-3">
              <h5 className="mb-0">Projects List</h5>
              <Badge bg="primary" className="px-2 py-1">
                {totalItems}
              </Badge>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted">Showing {currentProjects.length} of {totalItems} projects</span>
              <div className="vr"></div>
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  <MoreVertical size={14} />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item>
                    <Edit size={14} className="me-2" />
                    Bulk Edit
                  </Dropdown.Item>
                  <Dropdown.Item>
                    <Users size={14} className="me-2" />
                    Manage Teams
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item>
                    Export Data
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Card.Header>
          <Card.Body>
            <Table responsive className="modern-table">
              <thead>
                <tr>
                  <th>Project Number</th>
                  <th>Project Name</th>
                  <th>Tyler Number</th>
                  <th>Tyler Project Name</th>
                  <th style={{ textAlign: 'right' }}>Status</th>
                  <th>Type</th>
                  <th>Company</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentProjects.map((project) => {
                  const displayProjectNumber = project.projectNumber || project.tylerNumber || 'N/A';
                  const displayProjectName = project.projectName || project.tylerProjectName || 'Untitled Project';
                  const isFallbackNumber = !project.projectNumber && !!project.tylerNumber;
                  const isFallbackName = !project.projectName && !!project.tylerProjectName;
                  const grayStyle = { color: '#D3D3D3' };
                  return (
                    <tr key={project.id} style={{ cursor: 'pointer' }} onClick={() => handleViewProject(project)}>
                      <td>
                        <div className="fw-bold" style={isFallbackNumber ? grayStyle : undefined}>{displayProjectNumber}</div>
                      </td>
                      <td>
                        <div className="fw-bold" style={isFallbackName ? grayStyle : undefined}>{displayProjectName}</div>
                        <small className="text-muted">{project.summary || 'No description'}</small>
                      </td>
                      <td>
                        <div className="fw-bold">{project.tylerNumber || 'N/A'}</div>
                      </td>
                      <td>
                        <div>{project.tylerProjectName || 'N/A'}</div>
                      </td>
                      <td style={{ textAlign: 'right' }}>{getStatusBadge(project.status)}</td>
                      <td>
                        {project.isRetailer ? (
                          <Badge bg="info">Retailer</Badge>
                        ) : project.isCompany ? (
                          <Badge bg="primary">Company</Badge>
                        ) : (
                          <Badge bg="secondary">Unknown</Badge>
                        )}
                      </td>
                      <td>
                        <div className="small">Company ID: {project.companyId}</div>
                      </td>
                      <td>
                        <Dropdown>
                          <Dropdown.Toggle variant="light" size="sm" onClick={(e) => e.stopPropagation()}>
                            <MoreVertical size={16} />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={(e) => { e.stopPropagation(); handleViewProject(project); }}>
                              <Eye size={14} className="me-2" />
                              View Details
                            </Dropdown.Item>
                            <Dropdown.Item onClick={(e) => { e.stopPropagation(); }}>
                              <Edit size={14} className="me-2" />
                              Edit Project
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item 
                              className="text-danger"
                              onClick={(e) => { e.stopPropagation(); handleDeleteProject(project.id); }}
                            >
                              <Trash2 size={14} className="me-2" />
                              Delete Project
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Projects; 